/**
 * Replies to email one (docs/10 §8, docs/90 wave 3): what comes in, what the classifier calls it, where it stands
 * and what the staff console's queue shows. The engine is `@ff/prospects/replies`; `jobs` runs it against the
 * prospects database and the console and the app's Instantly webhook reach it through `JobsRpc` (REPLY-D1).
 */
import { z } from 'zod';
import { IsoDateTime, text } from './common.ts';

/**
 * docs/10 §8.1's labels, lower case. `no` is "not interested" and every unsubscribe; `hostile` covers HOSTILE,
 * LEGAL, HOW_DID_YOU_GET_MY_EMAIL, SUSPENSION and SPAM_ACCUSATION (the flag says which); `auto_reply` is an out of
 * office; `unclear` is what the rules and the model couldn't call with enough confidence.
 */
export const ProspectReplyLabel = z.enum(['yes_send', 'yes_with_question', 'question', 'not_now', 'wrong_person', 'no', 'hostile', 'auto_reply', 'other_language', 'unclear']);
export type ProspectReplyLabel = z.infer<typeof ProspectReplyLabel>;

/**
 * Where a reply stands. `building`: the yes path is making the recon. `awaiting_approval`: a ready email waits for
 * Fletcher to send it (autonomy stage 1, docs/10 §8.2). `needs_fletcher`: a person has to read and answer it.
 * `sent` and `done` need nothing more; `dismissed` is a person closing it.
 */
export const ProspectReplyState = z.enum(['new', 'building', 'awaiting_approval', 'needs_fletcher', 'sent', 'done', 'dismissed']);
export type ProspectReplyState = z.infer<typeof ProspectReplyState>;

export const ProspectReplySource = z.enum(['instantly', 'paste', 'fixture']);
export type ProspectReplySource = z.infer<typeof ProspectReplySource>;

/** What kind of message it was when it arrived: Instantly's webhook event, or how a person added it. */
export const ProspectReplyEventType = z.enum(['reply_received', 'auto_reply_received', 'lead_unsubscribed', 'paste', 'fixture']);
export type ProspectReplyEventType = z.infer<typeof ProspectReplyEventType>;

/** One reply as the engine takes it in, whatever its source. */
export const ProspectReplyIn = z.strictObject({
  source: ProspectReplySource,
  /** Instantly's `email_id` for a webhook; a hash of the sender and words for a paste, so a double paste is one reply. */
  externalId: z.string().min(1).max(200),
  receivedAt: IsoDateTime,
  fromEmail: z.string().min(3).max(254),
  /** The cold mailbox that got the reply (Instantly's `email_account`, the API's `eaccount`). */
  mailbox: z.string().max(254).nullable(),
  campaignId: z.string().max(100).nullable(),
  /** What Instantly's reply endpoint answers in the same thread (`reply_to_uuid`). Null for a paste. */
  replyToUuid: z.string().max(100).nullable(),
  subject: z.string().max(500).nullable(),
  text: z.string().max(20_000),
  event: ProspectReplyEventType,
  /** Our business id when the lead carried it (`ff_business`), else null and the engine matches by address. */
  businessRef: z.string().max(80).nullable(),
});
export type ProspectReplyIn = z.infer<typeof ProspectReplyIn>;

/** A reply's id in the prospects database. */
export const ProspectReplyId = z.string().regex(/^rpl_[a-z0-9]{6,40}$/).meta({ examples: ['rpl_k3v9q2m8x1w4'] });

export const ProspectReplyIntakeResult = z.object({
  id: ProspectReplyId,
  duplicate: z.boolean(),
  label: ProspectReplyLabel.nullable(),
  state: ProspectReplyState,
});
export type ProspectReplyIntakeResult = z.infer<typeof ProspectReplyIntakeResult>;

export const ProspectReplyHistoryRow = z.object({ at: IsoDateTime, kind: z.string().max(40), detail: z.string().max(1000).nullable(), actor: z.string().max(80) });

/** One reply in the staff console's queue: what came in, what we called it, what was sent and what's waiting. */
export const ProspectReplyQueueItem = z.object({
  id: ProspectReplyId,
  source: ProspectReplySource,
  receivedAt: IsoDateTime,
  fromEmail: z.string().max(254),
  subject: z.string().max(500).nullable(),
  body: z.string().max(20_000),
  businessId: z.string().max(80).nullable(),
  businessName: z.string().max(200).nullable(),
  label: ProspectReplyLabel.nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  classifiedBy: z.enum(['rules', 'ai', 'staff']).nullable(),
  rule: z.string().max(120).nullable(),
  flags: z.array(z.string().max(80)).max(20),
  state: ProspectReplyState,
  stateDetail: z.string().max(1000).nullable(),
  yesStep: z.string().max(20).nullable(),
  pageUrl: z.string().max(2048).nullable(),
  draftSubject: z.string().max(500).nullable(),
  draftBody: z.string().max(5000).nullable(),
  sentAt: IsoDateTime.nullable(),
  sentVia: z.string().max(40).nullable(),
  history: z.array(ProspectReplyHistoryRow).max(100),
});
export type ProspectReplyQueueItem = z.infer<typeof ProspectReplyQueueItem>;

export const ProspectReplyQueue = z.object({
  /** False when the prospects database isn't connected where this runs; the list is then empty and `notes` says why. */
  connected: z.boolean(),
  /** The autonomy ladder stage the engine runs at (docs/10 §8.2). */
  stage: z.number().int().min(1).max(4),
  /** Whether replies can go out through Instantly's API here, or Fletcher sends each one from the Unibox. */
  canSend: z.boolean(),
  /** How many replies stand in each state. */
  counts: z.object(Object.fromEntries(ProspectReplyState.options.map((s) => [s, z.number().int().min(0)])) as Record<ProspectReplyState, z.ZodNumber>),
  items: z.array(ProspectReplyQueueItem).max(200),
  notes: z.array(z.string().max(300)).max(10),
});
export type ProspectReplyQueue = z.infer<typeof ProspectReplyQueue>;

export const ProspectReplyQueueQuery = z.object({ view: z.enum(['waiting', 'all']).default('waiting') }).strict();

/** A reply Fletcher copied out of Instantly's Unibox when the webhook isn't on (Growth has no webhooks, docs/60 s124). */
export const ProspectReplyPasteBody = z.strictObject({
  fromEmail: z.string().trim().min(3).max(254),
  subject: z.string().trim().max(500).optional(),
  text: text(20_000, 1),
  /** When the reply arrived, if Fletcher knows. Defaults to now. */
  receivedAt: IsoDateTime.optional(),
});
export type ProspectReplyPasteBody = z.infer<typeof ProspectReplyPasteBody>;

/** What a person does with a reply in the queue. */
export const ProspectReplyActionBody = z.discriminatedUnion('action', [
  /** Send the ready email through Instantly, in the same thread, as written or edited. */
  z.strictObject({ action: z.literal('approve'), subject: z.string().trim().min(1).max(500).optional(), body: z.string().trim().min(10).max(5000).optional() }),
  /** Fletcher sent it himself from the Unibox. */
  z.strictObject({ action: z.literal('mark_sent'), note: z.string().trim().max(300).optional() }),
  /** The class was wrong: act on the right one (a suppression already made stays: it's permanent). */
  z.strictObject({ action: z.literal('reclassify'), label: ProspectReplyLabel, note: z.string().trim().max(300).optional() }),
  /** Run the yes path again from where it stopped. */
  z.strictObject({ action: z.literal('retry') }),
  /** Handled outside the queue, or nothing to do. */
  z.strictObject({ action: z.literal('dismiss'), note: text(300, 3) }),
]);
export type ProspectReplyActionBody = z.infer<typeof ProspectReplyActionBody>;

/**
 * A reply Fletcher pasted, as the engine takes it in. The same sender and words pasted twice are one reply: the
 * external id is a hash of both. One function for the console and the dry run, so the rule can't drift.
 */
export async function pastedReply(body: ProspectReplyPasteBody, now: Date): Promise<ProspectReplyIn> {
  const from = body.fromEmail.trim().toLowerCase();
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${from}
${body.text.trim()}`));
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  return {
    source: 'paste',
    externalId: `paste:${hex.slice(0, 40)}`,
    receivedAt: body.receivedAt ?? now.toISOString(),
    fromEmail: from,
    mailbox: null,
    campaignId: null,
    replyToUuid: null,
    subject: body.subject?.trim() || null,
    text: body.text,
    event: 'paste',
    businessRef: null,
  };
}
